import { nanoid } from '@reduxjs/toolkit';
import { ChartType } from '@/features/chartsView';
import type { Filters } from '@/features/filters';
import { prepareFilters } from '@/features/filters';
import { prepareDateRangeValueObject } from '@/shared/api';
import { DataStatus, isFinished, isLoading } from '@/shared/lib';
import type { AppThunk } from '@/shared/model';
import { createAppSlice } from '@/shared/model';
import type { SerializedDateRangeValueObject } from '@/shared/types';
import { DateRangePresets, DateScale, Lifetime } from '@/shared/types';
import * as CohortChartAPI from '../api/cohortChartAPI';
import type { CohortChartConfig, CohortDataResponse, ChartMeta } from './types';

interface State {
  meta:
    | {
        data: ChartMeta;
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
  chart: CohortChartConfig;
  isConfigInitialized: boolean;
}

const initialState: State = {
  meta: {
    data: null,
    status: DataStatus.idle,
  },
  isConfigInitialized: false,
  chart: {
    id: '',
    type: ChartType.cohort,
    dateScale: DateScale.DAY,
    dateRange: { preset: DateRangePresets.LAST_90_DAYS },
    lifetime: Lifetime.YEAR,
    probabilisticAttribution: true,
    splitDimension: '',
    primaryMetric: '',
    secondaryMetric: '',
    excludedSplitOptions: [],
    syncedYAxes: false,
    filters: [],
  },
};

export const cohortChartSlice = createAppSlice({
  name: 'cohortChart',
  initialState,
  reducers: (create) => ({
    initMeta: create.asyncThunk(
      (_: void) => {
        return CohortChartAPI.getMeta();
      },
      {
        pending: (state) => {
          state.meta.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.meta.data = action.payload;
          state.meta.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.meta.status = DataStatus.error;
          state.meta.data = null;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;
            const { status } = state.cohortChart.meta;

            if (isLoading(status) || isFinished(status)) {
              return false;
            }
          },
        },
      },
    ),
    updateDateScale: create.reducer<DateScale>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.dateScale = action.payload;
    }),
    updateDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.chart) {
          return;
        }

        state.chart.dateRange = action.payload;
      },
    ),
    updateLifetime: create.reducer<number>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.lifetime = action.payload;
    }),
    updateProbabilisticAttribution: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.probabilisticAttribution = action.payload;
    }),
    updateSplitDimension: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.splitDimension = action.payload;
    }),
    updatePrimaryMetric: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.primaryMetric = action.payload;

      if (
        state.meta.data?.config[state.chart.primaryMetric]?.type !==
        state.meta.data?.config[state.chart.secondaryMetric]?.type
      ) {
        state.chart.syncedYAxes = false;
      }
    }),
    updateSecondaryMetric: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.secondaryMetric = action.payload;

      if (
        state.meta.data?.config[state.chart.primaryMetric]?.type !==
        state.meta.data?.config[state.chart.secondaryMetric]?.type
      ) {
        state.chart.syncedYAxes = false;
      }
    }),
    updateExcludedSplitOptions: create.reducer<string[]>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.excludedSplitOptions = action.payload;
    }),
    updateSyncedYAxes: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.syncedYAxes = action.payload;
    }),
    updateFilters: create.reducer<Filters>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.filters = action.payload;
    }),
    updateChart: create.reducer<CohortChartConfig>((state, action) => {
      state.chart = action.payload;
      state.isConfigInitialized = true;
    }),
    setupVendorDefault: create.reducer<{ lifetimeTwoYearsExtension: boolean }>(
      (state, action) => {
        if (!isMetaFinished(state.meta)) {
          return;
        }

        state.chart = {
          id: nanoid(),
          type: ChartType.cohort,
          excludedSplitOptions: [],
          syncedYAxes: true,
          filters: [],
          dateScale: DateScale.DAY,
          dateRange: { preset: DateRangePresets.LAST_90_DAYS },
          lifetime: action.payload.lifetimeTwoYearsExtension
            ? Lifetime.TWO_YEARS
            : Lifetime.YEAR,
          probabilisticAttribution: true,
          splitDimension: state.meta.data.defaultAxis.dimension,
          primaryMetric: state.meta.data.defaultAxis.primaryMetric,
          secondaryMetric: state.meta.data.defaultAxis.secondaryMetric,
        };
        state.isConfigInitialized = true;
      },
    ),
    setupVendorDefaultWithOverride: create.reducer<{
      lifetimeTwoYearsExtension: boolean;
      override: Partial<CohortChartConfig>;
    }>((state, action) => {
      if (!isMetaFinished(state.meta)) {
        return;
      }

      state.chart = {
        id: nanoid(),
        type: ChartType.cohort,
        excludedSplitOptions: [],
        syncedYAxes: true,
        filters: [],
        dateScale: DateScale.DAY,
        dateRange: { preset: DateRangePresets.LAST_90_DAYS },
        lifetime: action.payload.lifetimeTwoYearsExtension
          ? Lifetime.TWO_YEARS
          : Lifetime.YEAR,
        probabilisticAttribution: true,
        splitDimension: state.meta.data.defaultAxis.dimension,
        primaryMetric: state.meta.data.defaultAxis.primaryMetric,
        secondaryMetric: state.meta.data.defaultAxis.secondaryMetric,
        ...action.payload.override,
      };
      state.isConfigInitialized = true;
    }),
    setupDefaultExcludedSplitOptions: create.reducer<string[]>(
      (state, action) => {
        const optionsToExclude = action.payload.slice(3);

        state.chart.excludedSplitOptions = optionsToExclude;
      },
    ),
    toggleExcludedSplitOptions: create.reducer<{
      options: string[];
      checked: boolean;
    }>((state, action) => {
      if (!state.chart) {
        return;
      }

      const excludedOptionsSet = new Set(state.chart.excludedSplitOptions);

      if (!action.payload.checked) {
        action.payload.options.forEach((option) => {
          excludedOptionsSet.add(option);
        });
      } else {
        action.payload.options.forEach((option) => {
          excludedOptionsSet.delete(option);
        });
      }

      state.chart.excludedSplitOptions = Array.from(excludedOptionsSet);
    }),
  }),
  selectors: {
    selectChart: (state) => state.chart,
    selectIsConfigInitialized: (state) => state.isConfigInitialized,
    selectMeta: (state) => state.meta.data,
    selectMetaDataStatus: (state) => state.meta.status,
    selectMetaIsLoading: (state) => isLoading(state.meta.status),
    selectMetaIsLoaded: (state) => isFinished(state.meta.status),
  },
});

function isMetaFinished(meta: State['meta']): meta is {
  data: ChartMeta;
  status: DataStatus.finished;
} {
  return meta.status === DataStatus.finished;
}

export const getChartData =
  (params: CohortChartConfig): AppThunk<Promise<CohortDataResponse>> =>
  (_dispatch, getState) => {
    const state = getState() as RootState;

    const { meta } = state.cohortChart;

    if (!isMetaFinished(meta)) {
      return Promise.reject(new Error('Meta not initialized'));
    }

    const { dimensionsFilters, metricsFilters } = prepareFilters(
      params.filters,
      meta.data.metrics,
      meta.data.dimensions,
    );

    return CohortChartAPI.getCohortData({
      split: params.splitDimension,
      metrics: [params.primaryMetric, params.secondaryMetric].filter(Boolean),
      dateScale: params.dateScale,
      dateRange: prepareDateRangeValueObject(params.dateRange),
      lifetime: params.lifetime,
      probabilisticAttribution: params.probabilisticAttribution,
      dimensionsFilters,
      metricsFilters,
    });
  };

export const {
  initMeta,
  updateDateScale,
  updateDateRange,
  updateLifetime,
  updateProbabilisticAttribution,
  updateSplitDimension,
  updatePrimaryMetric,
  updateSecondaryMetric,
  updateExcludedSplitOptions,
  updateSyncedYAxes,
  updateFilters,
  updateChart,
  setupVendorDefault,
  setupVendorDefaultWithOverride,
  toggleExcludedSplitOptions,
  setupDefaultExcludedSplitOptions,
} = cohortChartSlice.actions;
export const {
  selectChart,
  selectIsConfigInitialized,
  selectMeta,
  selectMetaDataStatus,
  selectMetaIsLoaded,
  selectMetaIsLoading,
} = cohortChartSlice.selectors;
